import React from 'react';
import { Link } from 'react-router-dom';

interface FeatureItem {
  title: string;
  description: string;
}

const FEATURES: FeatureItem[] = [
  {
    title: 'Upload payslip and roster',
    description:
      'Drop your PDF or image. We extract pay items and shift hours automatically using AWS Textract.',
  },
  {
    title: 'WA-aligned audit',
    description:
      'Calculations check your pay against Western Australian medical award rates, including penalties, overtime, and allowances.',
  },
  {
    title: 'Plain-English report',
    description:
      'See line-by-line discrepancies, the legal basis for each finding, and an estimated underpayment total.',
  },
  {
    title: 'Privacy-first',
    description:
      'Documents are redacted before storage. We never log personal identifiers, and you control retention.',
  },
];

const HOW_IT_WORKS: string[] = [
  'Create an account or sign in with your email.',
  'Upload one payslip and the matching roster for the same pay period.',
  'Wait under a minute for the audit to complete.',
  'Review the report and decide whether to raise a query with your employer.',
];

export function Landing() {
  return (
    <main className="landing-page" role="main" aria-labelledby="landing-heading">
      <header className="landing-header">
        <div className="landing-brand">
          <span className="landing-brand-name">AuditMyPay</span>
        </div>
        <nav className="landing-nav" aria-label="Primary">
          <Link to="/sign-in" className="landing-nav-link">
            Sign in
          </Link>
        </nav>
      </header>

      <section className="landing-hero" aria-labelledby="landing-heading">
        <h1 id="landing-heading" className="landing-hero-title">
          Audit your payslip in seconds.
        </h1>
        <p className="landing-hero-subtitle">
          Built for Australian medical professionals. Our MVP focuses on doctors in Western
          Australia.
        </p>
        <p className="landing-hero-description">
          AuditMyPay compares your payslip against your roster and the relevant WA medical award
          rates, then highlights any underpayments, missing penalties, or roster mismatches you
          can raise with your employer.
        </p>
        <div className="landing-cta-group" role="group" aria-label="Get started actions">
          <Link to="/sign-in" className="landing-cta landing-cta-primary">
            Get started
          </Link>
          <Link to="/sign-in" className="landing-cta landing-cta-secondary">
            I already have an account
          </Link>
        </div>
      </section>

      <section className="landing-features" aria-labelledby="features-heading">
        <h2 id="features-heading">What you get</h2>
        <ul className="landing-feature-list">
          {FEATURES.map((feature) => (
            <li key={feature.title} className="landing-feature-item">
              <h3 className="landing-feature-title">{feature.title}</h3>
              <p className="landing-feature-description">{feature.description}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="landing-how-it-works" aria-labelledby="how-heading">
        <h2 id="how-heading">How it works</h2>
        <ol className="landing-steps">
          {HOW_IT_WORKS.map((step, index) => (
            <li key={index} className="landing-step">
              {step}
            </li>
          ))}
        </ol>
      </section>

      <section className="landing-trust" aria-labelledby="trust-heading">
        <h2 id="trust-heading">Privacy and data handling</h2>
        <p>
          Your payslip and roster are redacted of personal identifiers before any storage. Files
          are processed in Australia (Sydney region) and you can delete your data at any time
          from the account page.
        </p>
        <p>
          AuditMyPay is informational and is not legal or industrial-relations advice. Always
          confirm findings with your union or HR before raising a formal claim.
        </p>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-cta">
          <Link to="/sign-in" className="landing-cta landing-cta-primary">
            Get started
          </Link>
        </div>
        <nav className="landing-footer-nav" aria-label="Footer">
          <a href="/privacy-policy" className="landing-footer-link">
            Privacy policy
          </a>
          <a href="/terms" className="landing-footer-link">
            Terms of use
          </a>
        </nav>
        <p className="landing-footer-copy">
          &copy; {new Date().getFullYear()} AuditMyPay. Built for Australian medical
          professionals.
        </p>
      </footer>
    </main>
  );
}
